import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { jsx as _jsx } from "react/jsx-runtime";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useHCaptchaStore } from "@faire/retailer-visitor-shared/stores/domain/HCaptchaStore";
import { getDocument } from "@faire/web--source/common/globals/getDocument";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { loadScript } from "@faire/web--source/common/loadScript";
import { trackSigninHcaptchaInit, trackSigninHcaptchaInitTraceDataRequested, trackSigninHcaptchaInitTraceDataRetrieved, trackSigninHcaptchaInitTraceRetrievalFailed } from "@faire/web-api--source/events/signin/actionUnknown/hcaptchaInit";
import { trackSigninHcaptchaRenderIsNotFunctionError } from "@faire/web-api--source/events/signin/error/hcaptchaRenderIsNotFunction";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import React, { useEffect, useRef, useState } from "react";
import { logHCaptchaError } from "@faire/retailer/lib/captchaUtils";
import { getSettingEnableHCaptchaIntegration } from "@faire/retailer/settings/getSettingEnableHCaptchaIntegration";
import { getSettingHCaptchaKey } from "@faire/retailer/settings/getSettingHCaptchaKey";
/**
 * Initializes HCpatcha and renders the invisible HCaptcha widget when:
 * - the session is not logged-in
 * - ENABLE_HCAPTCHA_INTEGRATION_221129 is true
 * - isHCaptchaEnabled is true
 *
 * isHCaptchaEnabled is set to true by hCaptcha callers (e.g. the sign-in form). The widget
 * is put high in the React tree (App.tsx) so it is not unmounted and remounted multiple
 * times in a session – this would consume more quota than necessary and cause unexpected
 * behaviours.
 */ export var HCaptcha = function() {
    var ref = useRef(null);
    var _useHCaptchaStore = _sliced_to_array(useHCaptchaStore([
        "widgetID",
        "isHCaptchaEnabled"
    ]), 2), _useHCaptchaStore_ = _useHCaptchaStore[0], savedWidgetId = _useHCaptchaStore_.widgetID, isHCaptchaEnabled = _useHCaptchaStore_.isHCaptchaEnabled, setHCaptchaStore = _useHCaptchaStore[1];
    // There are cases when the effect is run before the library is initialized,
    // which would result in `hcaptcha.render` to be unavailable. By re-rendering
    // once the library is initialized, we can ensure that the widget is rendered.
    var _useState = _sliced_to_array(useState(isHCaptchaInitialized()), 2), hasBeenInitialized = _useState[0], setHasBeenInitialized = _useState[1];
    var shouldGetWidgetId = isHCaptchaEnabled && !savedWidgetId && hasBeenInitialized;
    useEffect(function() {
        initializeHCaptcha(function() {
            return setHasBeenInitialized(true);
        });
    }, []);
    useEffect(function() {
        if (shouldGetWidgetId && ref.current) {
            var widgetID;
            try {
                widgetID = getWidgetId(ref.current);
            } catch (e) {
            // error is inferred if widgetID is undefined
            }
            setHCaptchaStore({
                widgetID: widgetID,
                initializationError: widgetID ? undefined : ILoginRequest.IHCaptchaData.HCaptchaError.RENDER_FAIL
            });
        }
    }, [
        shouldGetWidgetId,
        setHCaptchaStore
    ]);
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        style: {
            display: "none"
        }
    });
};
var HCAPTCHA_SCRIPT_NAME = "_hCaptcha";
var HCAPTCHA_SCRIPT_SRC = "https://js.hcaptcha.com/1/api.js?render=explicit";
var isHCaptchaInitialized = function() {
    var _getDocument;
    return !!((_getDocument = getDocument()) === null || _getDocument === void 0 ? void 0 : _getDocument.getElementById(HCAPTCHA_SCRIPT_NAME));
};
var initializeHCaptcha = /*#__PURE__*/ function() {
    var _ref = _async_to_generator(function(onInit) {
        var error;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    if (isLoggedInRetailer()) {
                        return [
                            2
                        ];
                    }
                    trackSigninHcaptchaInit();
                    if (!(getSettingEnableHCaptchaIntegration() && !isHCaptchaInitialized())) return [
                        3,
                        5
                    ];
                    _state.label = 1;
                case 1:
                    _state.trys.push([
                        1,
                        3,
                        4,
                        5
                    ]);
                    trackSigninHcaptchaInitTraceDataRequested();
                    return [
                        4,
                        loadScript(HCAPTCHA_SCRIPT_SRC, HCAPTCHA_SCRIPT_NAME)
                    ];
                case 2:
                    _state.sent();
                    trackSigninHcaptchaInitTraceDataRetrieved();
                    return [
                        3,
                        5
                    ];
                case 3:
                    error = _state.sent();
                    logHCaptchaError(error, ILoginRequest.IHCaptchaData.HCaptchaError.NO_SCRIPT);
                    trackSigninHcaptchaInitTraceRetrievalFailed();
                    return [
                        3,
                        5
                    ];
                case 4:
                    // we wanna signal that the library has been initialized, regardless of the outcome
                    // subsequent checks are made to ensure that the library is available
                    onInit();
                    return [
                        7
                    ];
                case 5:
                    return [
                        2
                    ];
            }
        });
    });
    return function initializeHCaptcha(onInit) {
        return _ref.apply(this, arguments);
    };
}();
var getWidgetId = function(container) {
    var _getGlobalProperty;
    var renderHCaptcha = (_getGlobalProperty = getGlobalProperty("hcaptcha")) === null || _getGlobalProperty === void 0 ? void 0 : _getGlobalProperty.render;
    if (!(typeof renderHCaptcha === "function")) {
        trackSigninHcaptchaRenderIsNotFunctionError();
        throw new Error(ILoginRequest.IHCaptchaData.HCaptchaError.RENDER_FAIL);
    }
    var widgetID = renderHCaptcha(container, {
        sitekey: getSettingHCaptchaKey(),
        size: "invisible",
        callback: function(_reponseToken) {}
    });
    return widgetID;
};
